import React, { useCallback, useEffect, useState } from "react";
import { BackButton, Button, Card, Chip } from "./ui";
import api from "./api";

/* -------------------------------------------------------------------------- */
/* PawCita SOS 1 — reportar y ver mascotas perdidas.                          */
/* Vistas internas: feed | create | detail. El menú inferior global sigue.    */
/* -------------------------------------------------------------------------- */

const RADII = [1, 3, 5];

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return "hace un momento";
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
}

const EMPTY_FORM = {
  dog_id: "",
  last_seen_location_label: "",
  last_seen_at: "",
  radius_km: 3,
  description: "",
  collar_or_harness: "",
  distinctive_features: "",
  behavior_notes: "",
  last_seen_lat: null,
  last_seen_lng: null,
};

export default function Sos({
  myDogs = [],
  isVerified = true,
  initialMode = "feed",
  initialAlertId = null,
  onAddDog,
  onBack,
  showToast,
}) {
  const [view, setView] = useState(initialMode === "create" ? "create" : "feed");
  const [alerts, setAlerts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const loadFeed = useCallback(() => {
    api.sos
      .list()
      .then(setAlerts)
      .catch(() => setAlerts([]));
  }, []);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    if (initialAlertId) {
      setSelectedId(initialAlertId);
      setView("detail");
    }
  }, [initialAlertId]);

  const openDetail = (id) => {
    setSelectedId(id);
    setView("detail");
  };

  const onCreated = () => {
    loadFeed();
    setView("feed");
    showToast?.("Alerta SOS activada 🆘", "coral");
  };

  if (view === "create") {
    return (
      <SosCreate
        myDogs={myDogs}
        isVerified={isVerified}
        onAddDog={onAddDog}
        onBack={() => setView("feed")}
        onCreated={onCreated}
        showToast={showToast}
      />
    );
  }

  if (view === "detail") {
    return (
      <SosDetail
        alertId={selectedId}
        isVerified={isVerified}
        showToast={showToast}
        onBack={() => {
          setView("feed");
          loadFeed();
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="sos-feed-screen">
      <div className="mb-4 flex items-center justify-between">
        <BackButton onClick={onBack} testId="sos-back-btn" />
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-[#E11D48] to-[#FF5C5C] p-5 text-white shadow-[0_16px_40px_-16px_rgba(225,29,72,0.7)]">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-white/80">🆘 PawCita SOS</p>
        <h2 className="mt-1 font-display text-2xl font-black leading-tight">
          Red comunitaria para encontrar peluditos perdidos
        </h2>
        <p className="mt-2 text-sm text-white/90">
          ¿Perdiste a tu peludito? Activa una alerta y avisa a la comunidad PawCita.
        </p>
        <button
          onClick={() => setView("create")}
          data-testid="sos-activate-btn"
          className="mt-4 w-full rounded-full bg-white px-4 py-3 text-sm font-black text-[#E11D48] transition active:translate-y-[1px]"
        >
          Activar alerta SOS
        </button>
      </div>

      <h3 className="mt-6 font-display text-xl font-black text-[#0B2D4D]">Alertas SOS activas</h3>

      <SosLocationPrompt showToast={showToast} />

      <div className="mt-4 space-y-4" data-testid="sos-alerts-list">
        {alerts === null && <p className="text-sm text-slate-400">Cargando alertas…</p>}
        {alerts !== null && alerts.length === 0 && (
          <Card className="p-8 text-center" data-testid="sos-empty">
            <p className="font-display text-xl font-black text-[#0B2D4D]">Todo en calma 💚</p>
            <p className="mt-2 text-sm text-slate-500">
              Por suerte no hay alertas SOS activas en este momento.
            </p>
          </Card>
        )}
        {(alerts || []).map((a) => (
          <SosCard key={a.id} alert={a} onOpen={() => openDetail(a.id)} />
        ))}
      </div>
    </div>
  );
}

function SosCard({ alert, onOpen }) {
  return (
    <Card className="overflow-hidden" data-testid={`sos-card-${alert.id}`}>
      <div className="flex items-center gap-2 bg-rose-50 px-4 py-2 text-sm font-black text-[#E11D48]">
        🆘 {alert.dog_name} está perdido
      </div>
      <div className="flex gap-4 p-4">
        <img
          src={alert.image || "/dog-placeholder.svg"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/dog-placeholder.svg";
          }}
          alt={alert.dog_name}
          className="h-24 w-24 flex-shrink-0 rounded-2xl object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-black text-[#0B2D4D]">{alert.dog_name}</p>
          {alert.dog_breed && <p className="text-xs text-slate-500">{alert.dog_breed}</p>}
          <p className="mt-1 text-sm text-slate-600">📍 {alert.last_seen_location_label}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>🕒 {timeAgo(alert.created_at)}</span>
            <Chip tone="coral">Radio: {alert.radius_km} km</Chip>
          </div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <Button variant="primary" onClick={onOpen} testId={`sos-open-${alert.id}`} className="w-full">
          Ver alerta
        </Button>
      </div>
    </Card>
  );
}

function SosDetail({ alertId, isVerified, showToast, onBack }) {
  const [alert, setAlert] = useState(null);
  const [sightings, setSightings] = useState([]);
  const [sub, setSub] = useState("detail"); // detail | sighting | chat
  const [chatCtx, setChatCtx] = useState(null); // {chatId, label}
  const [mapSighting, setMapSighting] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([api.sos.detail(alertId), api.sos.sightings(alertId)]);
      setAlert(a);
      setSightings(s);
    } catch (e) {
      showToast?.(e.message || "No se pudo abrir la alerta", "navy");
      onBack();
    }
  }, [alertId]); // eslint-disable-line

  useEffect(() => {
    load();
  }, [load]);

  // Refresh expiring SOS photo links without navigating away.
  useEffect(() => {
    let active = true;

    const renewPhotos = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const latest = await api.sos.sightings(alertId);
        if (active) setSightings(latest);
      } catch {
        // Keep the current sightings if a background refresh fails.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void renewPhotos();
      }
    };

    const timer = window.setInterval(() => {
      void renewPhotos();
    }, 90000);

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [alertId]);

  const markFound = async () => {
    if (!window.confirm(`¿Confirmas que ${alert.dog_name || "tu mascota"} fue encontrado?`)) return;
    try {
      await api.sos.markFound(alertId);
      showToast?.("💚 ¡Qué alegría! Marcado como encontrado", "mint");
      load();
    } catch (e) {
      showToast?.(e.message || "No se pudo actualizar", "navy");
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/?sos=${alertId}`;
    const text = `🆘 Mascota perdida\n${alert.dog_name}\nÚltima vez visto en ${alert.last_seen_location_label}\nAyúdanos a encontrarlo en PawCita.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "PawCita SOS", text, url });
      } catch (e) {
        /* el usuario canceló el share sheet nativo */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast?.("Enlace de la alerta copiado", "mint");
    } catch (e) {
      showToast?.("No se pudo copiar el enlace. Cópialo manualmente.", "navy");
    }
  };

  const askReporter = async (s) => {
    try {
      const d = await api.sos.chatFromSighting(s.id);
      setChatCtx({ chatId: d.chatId });
      setSub("chat");
    } catch (e) {
      showToast?.(e.message || "No se pudo abrir la conversación", "navy");
    }
  };

  if (!alert) {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6">
        <BackButton onClick={onBack} testId="sos-detail-back" />
        <p className="mt-6 text-sm text-slate-400">Cargando alerta…</p>
      </div>
    );
  }

  if (sub === "sighting") {
    return (
      <SosSightingForm
        alert={alert}
        showToast={showToast}
        onBack={() => setSub("detail")}
        onDone={() => {
          setSub("detail");
          load();
        }}
      />
    );
  }

  if (sub === "chat") {
    return (
      <SosChat
        alert={alert}
        isVerified={isVerified}
        chatCtx={chatCtx}
        showToast={showToast}
        onBack={() => setSub("detail")}
      />
    );
  }

  const found = alert.status === "found" || alert.status === "closed";
  const canHelp = !alert.is_owner && !found;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="sos-detail-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} testId="sos-detail-back" />
      </div>

      <Card className="overflow-hidden">
        <div className="relative h-72 w-full overflow-hidden bg-slate-100">
          <img
            src={alert.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            onClick={() => alert.image && setPhotoOpen(true)}
            alt={alert.dog_name}
            data-testid="sos-photo-thumb"
            className="h-full w-full cursor-pointer object-cover"
          />
          <div className="absolute left-3 top-3 rounded-full bg-[#E11D48] px-3 py-1 text-xs font-black text-white shadow">
            🆘 MASCOTA PERDIDA
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <h2 className="font-display text-3xl font-black text-[#0B2D4D]">{alert.dog_name}</h2>
            <p className="text-sm text-slate-500">
              {[alert.dog_breed, alert.dog_age != null ? `${alert.dog_age} años` : ""].filter(Boolean).join(" · ")}
            </p>
          </div>

          {found ? (
            <div className="rounded-2xl bg-emerald-50 px-4 py-4 text-center text-sm font-black text-emerald-700" data-testid="sos-found-banner">
              💚 Mascota encontrada
            </div>
          ) : (
            <div className="rounded-2xl bg-rose-50 px-4 py-3 text-center text-sm font-bold text-[#E11D48]">
              PawCita SOS está movilizando a la comunidad 🐾
            </div>
          )}

          <Field label="Última vez visto" value={alert.last_seen_location_label || "Ubicación no especificada"} />
          {alert.last_seen_at && <Field label="¿Cuándo?" value={alert.last_seen_at} />}
          <Field label="Radio de búsqueda" value={`${alert.radius_km} km`} />
          {alert.description && <Field label="Información importante" value={alert.description} />}
          {alert.distinctive_features && <Field label="Características distintivas" value={alert.distinctive_features} />}
          {alert.collar_or_harness && <Field label="Collar / arnés" value={alert.collar_or_harness} />}
          {alert.behavior_notes && <Field label="Comportamiento" value={alert.behavior_notes} />}

          {/* Acciones comunitarias */}
          {canHelp && (
            <div className="grid grid-cols-2 gap-2">
              <Button variant="primary" onClick={() => setSub("sighting")} testId="sos-report-sighting-btn" className="w-full">
                👀 Lo vi
              </Button>
              <Button variant="navy" onClick={() => { setChatCtx(null); setSub("chat"); }} testId="sos-have-info-btn" className="w-full">
                💬 Tengo información
              </Button>
            </div>
          )}
          <Button variant="outline" onClick={share} testId="sos-share-btn" className="w-full">
            📤 Compartir alerta
          </Button>

          {(alert.is_owner || __hasAdmin()) && (
            <Button variant="outline" onClick={() => { setChatCtx(null); setSub("chat"); }} testId="sos-owner-chats-btn" className="w-full">
              💬 Ver conversaciones
            </Button>
          )}

          {alert.is_owner && !found && (
            <Button variant="mint" onClick={markFound} testId="sos-mark-found-btn" className="w-full">
              💚 ¡Ya apareció!
            </Button>
          )}
        </div>
      </Card>

      {/* Timeline de avistamientos */}
      <div className="mt-6" data-testid="sos-sightings-section">
        <h3 className="font-display text-xl font-black text-[#0B2D4D]">👀 Avistamientos</h3>
        {sightings.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Aún no hay avistamientos reportados.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {sightings.map((s) => (
              <Card key={s.id} className="p-4" data-testid={`sos-sighting-${s.id}`}>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>🕒 {timeAgo(s.created_at)}</span>
                  {s.seen_at && <span>{s.seen_at}</span>}
                </div>
                {alert.is_owner && s.lat != null && s.lng != null ? (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-[#0B2D4D]">📍 Ubicación del avistamiento</p>
                    <button
                      onClick={() => setMapSighting(s)}
                      data-testid={`sos-sighting-map-${s.id}`}
                      className="flex-shrink-0 rounded-full bg-[#E11D48] px-3 py-1 text-xs font-black text-white"
                    >
                      Ver en mapa
                    </button>
                  </div>
                ) : (
                  <p className="mt-1 text-sm font-bold text-[#0B2D4D]">📍 {s.location_label}</p>
                )}
                {s.note && <p className="mt-1 text-sm text-slate-600">{s.note}</p>}
                {alert.is_owner && s.has_reporter && alert.status === "active" && (
                  <button
                    onClick={() => askReporter(s)}
                    data-testid={`sos-ask-reporter-${s.id}`}
                    className="mt-2 w-full rounded-full border border-[#0B2D4D] px-3 py-2 text-xs font-black text-[#0B2D4D] transition active:translate-y-[1px]"
                  >
                    💬 Preguntar al reportante
                  </button>
                )}
                {s.image && (
                  <img
                    key={s.image}
                    src={s.image}
                    alt="avistamiento"
                    className="mt-2 h-40 w-full rounded-2xl object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {mapSighting && (
        <SosMapModal sighting={mapSighting} dogName={alert.dog_name} onClose={() => setMapSighting(null)} />
      )}
      {photoOpen && (
        <SosPhotoLightbox src={alert.image} name={alert.dog_name} onClose={() => setPhotoOpen(false)} />
      )}
    </div>
  );
}

function SosPhotoLightbox({ src, name, onClose }) {
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95"
      data-testid="sos-photo-lightbox"
      onClick={onClose}
      style={{ touchAction: "pinch-zoom" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Cerrar imagen"
        data-testid="sos-photo-close"
        className="absolute right-4 top-4 z-[71] flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl text-white"
      >
        ×
      </button>
      <img
        src={src}
        alt={name}
        onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
        className="max-h-[92vh] max-w-[96vw] object-contain transition-transform duration-200"
        style={{ transform: zoomed ? "scale(2)" : "scale(1)", cursor: zoomed ? "zoom-out" : "zoom-in" }}
      />
    </div>
  );
}

function SosMapModal({ sighting, dogName, onClose }) {
  const lat = sighting.lat;
  const lng = sighting.lng;
  const d = 0.004;
  const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
  const osm = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  const maps = `https://www.google.com/maps?q=${lat},${lng}`;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      data-testid="sos-map-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3">
          <p className="font-display text-lg font-black text-[#0B2D4D]">📍 Avistamiento de {dogName}</p>
          <button onClick={onClose} data-testid="sos-map-close" className="text-2xl text-slate-400">×</button>
        </div>
        <iframe
          title="mapa-avistamiento"
          src={osm}
          className="h-72 w-full border-0"
          loading="lazy"
        />
        <div className="p-4">
          <a
            href={maps}
            target="_blank"
            rel="noreferrer"
            data-testid="sos-map-open-maps"
            className="block w-full rounded-full bg-[#0B2D4D] px-4 py-3 text-center text-sm font-black text-white"
          >
            Abrir en Maps
          </a>
        </div>
      </div>
    </div>
  );
}
function __hasAdmin() {
  return false; // admin gestiona conversaciones desde el panel Admin → SOS
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-[#0B2D4D]">{value}</p>
    </div>
  );
}

function SosSightingForm({ alert, showToast, onBack, onDone }) {
  const [form, setForm] = useState({ location_label: "", seen_at: "", note: "", lat: null, lng: null });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [err, setErr] = useState("");

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      showToast?.("Tu navegador no permite ubicación. Escríbela manualmente.", "navy");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          location_label: f.location_label || "Ubicación compartida por GPS",
        }));
        setLocating(false);
        showToast?.("Ubicación capturada 📍", "mint");
      },
      () => { setLocating(false); showToast?.("No pudimos obtener tu ubicación.", "navy"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async () => {
    setErr("");
    if (!form.location_label.trim()) return setErr("Indica dónde viste a la mascota.");
    setSaving(true);
    try {
      const created = await api.sos.addSighting(alert.id, form);
      if (file) {
        try { await api.sos.uploadSightingPhoto(created.id, file); } catch { showToast?.("Avistamiento guardado, pero la foto no se subió.", "navy"); }
      }
      showToast?.("¡Gracias! Avistamiento reportado 👀", "mint");
      onDone();
    } catch (e) {
      setErr(e.message || "No se pudo reportar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="sos-sighting-form">
      <div className="mb-4"><BackButton onClick={onBack} label="Volver a la alerta" testId="sos-sighting-back" /></div>
      <h2 className="font-display text-2xl font-black text-[#0B2D4D]">👀 Vi a {alert.dog_name}</h2>
      <div className="mt-5 space-y-4">
        <LabeledInput label="¿Dónde lo viste?" value={form.location_label}
          onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
          placeholder="Ej: Vía España, cerca del super" testId="sos-sighting-location" />
        <button onClick={useMyLocation} disabled={locating} data-testid="sos-sighting-use-location"
          className="w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-[#0B2D4D] transition hover:bg-slate-50 disabled:opacity-60">
          {locating ? "Obteniendo ubicación…" : "📍 Usar mi ubicación actual"}
        </button>
        <LabeledInput label="¿Cuándo lo viste?" type="datetime-local" value={form.seen_at}
          onChange={(e) => setForm((f) => ({ ...f, seen_at: e.target.value }))} testId="sos-sighting-datetime" />
        <LabeledTextarea label="Comentario (opcional)" value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          placeholder="Iba corriendo hacia el parque, se veía asustado…" testId="sos-sighting-note" />
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">Foto (opcional)</span>
          <input type="file" accept="image/*" data-testid="sos-sighting-photo"
            onChange={(e) => setFile(e.target.files && e.target.files[0])}
            className="w-full text-sm text-slate-500" />
        </label>
        {err && <p className="text-sm font-semibold text-rose-600" data-testid="sos-sighting-error">{err}</p>}
        <Button variant="primary" onClick={submit} disabled={saving} testId="sos-sighting-submit" className="w-full">
          {saving ? "Enviando…" : "👀 Reportar avistamiento"}
        </Button>
      </div>
    </div>
  );
}

function SosChat({ alert, isVerified, chatCtx, showToast, onBack }) {
  const [mode, setMode] = useState(chatCtx ? "thread" : alert.is_owner ? "list" : "thread");
  const [chatId, setChatId] = useState(chatCtx ? chatCtx.chatId : null);
  const [chatList, setChatList] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);

  const loadThread = useCallback(async (id) => {
    try { setThread(await api.sos.chatMessages(id)); }
    catch (e) { showToast?.(e.message || "No se pudo abrir el chat", "navy"); }
  }, [showToast]);

  useEffect(() => {
    (async () => {
      if (chatCtx?.chatId) {
        // apertura directa a un hilo concreto (p.ej. dueño → "Preguntar al reportante")
        setChatId(chatCtx.chatId);
        await loadThread(chatCtx.chatId);
        setMode("thread");
      } else if (alert.is_owner) {
        // dueño: siempre lista de conversaciones
        try { const d = await api.sos.chats(alert.id); setChatList(d.chats || []); } catch { setChatList([]); }
        setMode("list");
      } else {
        // ayudante inicia/obtiene su conversación
        try {
          const d = await api.sos.startChat(alert.id);
          setChatId(d.chatId);
          setThread(d);
          setMode("thread");
        } catch (e) {
          showToast?.(e.message || "No se pudo iniciar la conversación", "navy");
          onBack();
        }
      }
    })();
  }, []); // eslint-disable-line

  const openThread = async (id) => {
    setChatId(id);
    setMode("thread");
    await loadThread(id);
  };

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setErr("");
    setSending(true);
    try {
      await api.sos.sendChatMessage(chatId, t);
      setText("");
      await loadThread(chatId);
    } catch (e) {
      setErr(e.message || "No se pudo enviar.");
    } finally {
      setSending(false);
    }
  };

  if (mode === "list") {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="sos-chatlist">
        <div className="mb-4"><BackButton onClick={onBack} label="Volver a la alerta" testId="sos-chatlist-back" /></div>
        <h2 className="font-display text-2xl font-black text-[#0B2D4D]">💬 Conversaciones SOS</h2>
        {chatList === null ? (
          <p className="mt-4 text-sm text-slate-400">Cargando…</p>
        ) : chatList.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500" data-testid="sos-chatlist-empty">Aún no hay conversaciones sobre esta alerta.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {chatList.map((c) => (
              <button key={c.chatId} onClick={() => openThread(c.chatId)} data-testid={`sos-chat-open-${c.chatId}`}
                className="flex w-full items-center justify-between rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-sm transition hover:bg-slate-50">
                <div className="min-w-0">
                  <p className="font-bold text-[#0B2D4D]">{c.label}</p>
                  <p className="truncate text-sm text-slate-500">{c.lastMessage || "Sin mensajes aún"}</p>
                </div>
                <span className="text-slate-300">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-3xl flex-col px-4 pt-4 sm:px-6" data-testid="sos-chat">
      <div className="mb-3">
        <BackButton onClick={alert.is_owner && !chatCtx ? () => setMode("list") : onBack} label="Volver" testId="sos-chat-back" />
      </div>
      <p className="mb-2 text-sm font-bold text-[#0B2D4D]">💬 Sobre {alert.dog_name}</p>
      <div className="flex-1 space-y-2 overflow-y-auto rounded-2xl bg-slate-50 p-3" data-testid="sos-chat-messages">
        {thread === null ? (
          <p className="text-sm text-slate-400">Cargando…</p>
        ) : thread.messages.length === 0 ? (
          <p className="text-center text-sm text-slate-400">Escribe el primer mensaje para coordinar de forma segura.</p>
        ) : (
          thread.messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-[#0B2D4D] text-white" : "bg-white text-[#0B2D4D] shadow-sm"}`}>
                {!m.mine && <span className="mb-0.5 block text-[10px] font-bold text-slate-400">{m.from}</span>}
                {m.text}
                <span className="mt-0.5 block text-[10px] opacity-60">{m.time}</span>
              </div>
            </div>
          ))
        )}
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-rose-600" data-testid="sos-chat-error">{err}</p>}
      {thread && thread.readOnly ? (
        <p className="my-3 rounded-full bg-emerald-50 px-4 py-2 text-center text-sm font-bold text-emerald-700">
          Alerta cerrada · conversación de solo lectura
        </p>
      ) : (
        <div className="my-3 flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Escribe un mensaje seguro…"
            data-testid="sos-chat-input"
            className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#E11D48]"
          />
          <Button variant="primary" onClick={send} disabled={sending} testId="sos-chat-send" className="px-5">
            Enviar
          </Button>
        </div>
      )}
    </div>
  );
}

function SosCreate({ myDogs, isVerified, onAddDog, onBack, onCreated, showToast }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, dog_id: myDogs[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      showToast?.("Tu navegador no permite ubicación. Escríbela manualmente.", "navy");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          last_seen_lat: pos.coords.latitude,
          last_seen_lng: pos.coords.longitude,
          last_seen_location_label: f.last_seen_location_label || "Ubicación compartida por GPS",
        }));
        setLocating(false);
        showToast?.("Ubicación capturada 📍", "mint");
      },
      () => {
        setLocating(false);
        showToast?.("No pudimos obtener tu ubicación. Escríbela manualmente.", "navy");
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async () => {
    setErr("");
    if (!form.dog_id) return setErr("Selecciona tu mascota.");
    if (!form.last_seen_location_label.trim()) return setErr("Indica dónde la viste por última vez.");
    setSaving(true);
    try {
      await api.sos.create({
        ...form,
        radius_km: Number(form.radius_km),
      });
      onCreated();
    } catch (e) {
      setErr(e.message || "No se pudo crear la alerta.");
    } finally {
      setSaving(false);
    }
  };

  const gate = !isVerified || myDogs.length === 0;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="sos-create-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} label="Volver a SOS" testId="sos-create-back" />
      </div>

      <h2 className="font-display text-2xl font-black text-[#0B2D4D]">🆘 Activar alerta SOS</h2>

      {!isVerified && (
        <Card className="mt-4 p-6 text-center" data-testid="sos-need-verify">
          <p className="text-sm text-slate-600">Verifica tu correo para activar PawCita SOS.</p>
        </Card>
      )}

      {isVerified && myDogs.length === 0 && (
        <Card className="mt-4 p-6 text-center" data-testid="sos-need-dog">
          <p className="font-display text-lg font-black text-[#0B2D4D]">
            Primero registra a tu peludito para activar PawCita SOS.
          </p>
          <Button className="mt-4" onClick={onAddDog} testId="sos-add-dog-btn">
            Agregar mascota
          </Button>
        </Card>
      )}

      {!gate && (
        <div className="mt-5 space-y-4">
          <div>
            <p className="mb-2 text-sm font-bold text-[#0B2D4D]">Selecciona tu mascota</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="sos-dog-picker">
              {myDogs.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setForm((f) => ({ ...f, dog_id: d.id }))}
                  data-testid={`sos-dog-${d.id}`}
                  className={`overflow-hidden rounded-2xl border-2 text-left transition ${
                    form.dog_id === d.id ? "border-[#E11D48]" : "border-transparent"
                  }`}
                >
                  <img
                    src={d.image || "/dog-placeholder.svg"}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/dog-placeholder.svg";
                    }}
                    alt={d.name}
                    className="h-24 w-full object-cover"
                  />
                  <span className="block truncate px-2 py-1.5 text-xs font-bold text-[#0B2D4D]">
                    {d.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <LabeledInput
            label="¿Dónde fue? (última vez visto)"
            value={form.last_seen_location_label}
            onChange={set("last_seen_location_label")}
            placeholder="Ej: Parque Omar, entrada principal"
            testId="sos-location-input"
          />
          <button
            onClick={useMyLocation}
            disabled={locating}
            data-testid="sos-use-location-btn"
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-[#0B2D4D] transition hover:bg-slate-50 disabled:opacity-60"
          >
            {locating ? "Obteniendo ubicación…" : "📍 Usar mi ubicación actual"}
          </button>

          <LabeledInput
            label="¿Cuándo?"
            type="datetime-local"
            value={form.last_seen_at}
            onChange={set("last_seen_at")}
            testId="sos-datetime-input"
          />

          <div>
            <p className="mb-2 text-sm font-bold text-[#0B2D4D]">¿Hasta qué distancia quieres avisar?</p>
            <div className="flex gap-2" data-testid="sos-radius-picker">
              {RADII.map((r) => (
                <button
                  key={r}
                  onClick={() => setForm((f) => ({ ...f, radius_km: r }))}
                  data-testid={`sos-radius-${r}`}
                  className={`flex-1 rounded-2xl border px-3 py-3 text-sm font-black transition ${
                    Number(form.radius_km) === r
                      ? "border-[#E11D48] bg-rose-50 text-[#E11D48]"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  {r} km
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              PawCita avisará a usuarios cercanos que tengan notificaciones y ubicación habilitadas. Tu ubicación exacta no será compartida.
            </p>
          </div>

          <LabeledTextarea
            label="Información importante"
            value={form.description}
            onChange={set("description")}
            placeholder="Cuenta qué pasó y cómo ayudar a encontrarlo."
            testId="sos-description-input"
          />
          <LabeledInput
            label="Collar / arnés (opcional)"
            value={form.collar_or_harness}
            onChange={set("collar_or_harness")}
            testId="sos-collar-input"
          />
          <LabeledInput
            label="Características distintivas (opcional)"
            value={form.distinctive_features}
            onChange={set("distinctive_features")}
            testId="sos-features-input"
          />
          <LabeledTextarea
            label="Comportamiento (opcional)"
            value={form.behavior_notes}
            onChange={set("behavior_notes")}
            testId="sos-behavior-input"
          />

          {err && (
            <p className="text-sm font-semibold text-rose-600" data-testid="sos-create-error">
              {err}
            </p>
          )}

          <Button
            variant="primary"
            onClick={submit}
            disabled={saving}
            testId="sos-submit-btn"
            className="w-full"
          >
            {saving ? "Activando…" : "🆘 Activar alerta SOS"}
          </Button>
        </div>
      )}
    </div>
  );
}

function SosLocationPrompt({ showToast }) {
  const SNOOZE_KEY = "pawcita:sos-loc-snooze";
  const snoozed = () => {
    try {
      const t = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      return Date.now() - t < 24 * 3600 * 1000;
    } catch { return false; }
  };
  const [status, setStatus] = useState(null); // {hasLocation, stale}
  const [hidden, setHidden] = useState(snoozed());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (hidden) return;
    api.users.locationStatus().then(setStatus).catch(() => setStatus({ hasLocation: false, stale: false }));
  }, [hidden]);

  if (hidden || !status) return null;
  // Nada que hacer si ya tiene ubicación fresca.
  if (status.hasLocation && !status.stale) return null;

  const isStale = status.hasLocation && status.stale;
  const title = isStale ? "Actualiza tu zona de ayuda 🐾" : "Ayuda a encontrar mascotas cerca de ti";
  const text = isStale
    ? "Actualiza tu ubicación para seguir recibiendo alertas de mascotas perdidas cerca de ti."
    : "Con tu ubicación (y notificaciones), te avisamos si se pierde una mascota en tu zona. Tu ubicación exacta nunca se comparte.";
  const cta = isStale ? "Actualizar ubicación" : "Activar";

  const dismiss = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch {}
    setHidden(true);
  };

  const enable = () => {
    if (!navigator.geolocation) {
      showToast?.("Tu navegador no permite ubicación.", "navy");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.users.setLocation(pos.coords.latitude, pos.coords.longitude);
          showToast?.(isStale ? "Ubicación actualizada" : "¡Gracias! Te avisaremos si hay mascotas perdidas cerca 🐾", "mint");
        } catch (e) {
          showToast?.("No se pudo guardar tu ubicación.", "navy");
        }
        setBusy(false);
        setHidden(true);
      },
      () => { setBusy(false); showToast?.("Sin ubicación no podremos avisarte de mascotas cercanas.", "navy"); dismiss(); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="mt-4 flex items-center gap-3 rounded-3xl border border-emerald-100 bg-emerald-50 p-4" data-testid="sos-location-prompt">
      <span className="text-2xl">📍</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-[#0B2D4D]">{title}</p>
        <p className="text-xs text-slate-600">{text}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <button onClick={enable} disabled={busy} data-testid="sos-location-enable"
          className="rounded-full bg-[#67D5B5] px-3 py-1.5 text-xs font-black text-[#0B2D4D] disabled:opacity-60">
          {busy ? "…" : cta}
        </button>
        <button onClick={dismiss} data-testid="sos-location-dismiss"
          className="rounded-full px-3 py-1 text-xs font-bold text-slate-400">
          Ahora no
        </button>
      </div>
    </div>
  );
}

function LabeledInput({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">{label}</span>
      <input
        data-testid={testId}
        {...props}
        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#E11D48]"
      />
    </label>
  );
}

function LabeledTextarea({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">{label}</span>
      <textarea
        data-testid={testId}
        rows={3}
        {...props}
        className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#E11D48]"
      />
    </label>
  );
}
